import { useMutation } from "@apollo/client";
import { OverlayToaster, Position } from "@blueprintjs/core";
import {
    RestudySource,
    segmentTrackRestudySwitchToScopeClicked
} from "analytics/analyticTrackEvents";
import { useBusPageConfiguration } from "contexts/BusPageContext/BusPageConfigurationState";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import {
    CreateUserScopeConfigMutation,
    CreateUserScopeConfigMutationVariables
} from "generated/graphql";
import { loader } from "graphql.macro";
import { useState } from "react";
import { GeneratorType } from "types/generatorType";
import { QueueProject } from "types/queueProjectsType";
import { Scope } from "types/scope";
import { getCapacityCostsScopeViewFromRegionAndGenerator } from "types/scopeViewType";
import { buildQueueProjectInsertInputs } from "utils/queueProjectUtils";
import {
    buildSpeculativeQueueProjectInsertInputs,
    getSpeculativeFuelTypeLabel,
    SpeculativeProjectDraft
} from "utils/speculativeProjectUtils";

const CreateUserScopeToaster = OverlayToaster.create({
    position: Position.TOP
});

const CREATE_USER_SCOPE_CONFIG = loader(
    "src/graphql/createUserScopeConfig.graphql"
);
const GET_SCOPES_FOR_BUS = loader("src/graphql/getScopesForBus.graphql");
const GET_BUS_SCOPE_VIEWS = loader("src/graphql/getBusScopeViews.graphql");

const slugify = (value: string): string =>
    value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "");

const hasOneAlphanumeric = (value: string): boolean => /[a-z0-9]/.test(value);

export type ScopeNameResult =
    | { readonly success: true; readonly scopeName: string }
    | { readonly success: false; readonly error: string };

export const displayNameToScopeName = (
    displayName: string,
    teamName: string,
    niraBusId: string,
    existingScopeNames: ReadonlyArray<string>
): ScopeNameResult => {
    const nameSlug = slugify(displayName);
    const scopeName = `${slugify(
        teamName
    )}__${nameSlug}__${niraBusId.toLowerCase()}`;
    if (!hasOneAlphanumeric(nameSlug)) {
        return {
            success: false,
            error: "Scope name must have at least one alphanumeric character"
        };
    }
    if (existingScopeNames.includes(scopeName)) {
        return {
            success: false,
            error: "A scope with this name already exists"
        };
    }
    return { success: true, scopeName };
};

export interface ValidatedNewScopeRequest {
    readonly displayName: string;
    readonly notes: string;
    readonly selectedFuelTypes: ReadonlyArray<GeneratorType>;
    readonly baseScope: Scope;
    readonly editedQueueProjects: ReadonlyArray<QueueProject>;
    readonly newProjects: ReadonlyArray<SpeculativeProjectDraft>;
}

/** Create a local study, toasting the outcome; resolves true on success. */
export const useCreateUserScope = () => {
    const {
        allScopesForBus,
        niraBusId,
        bus,
        setSelectedScope,
        setSelectedGeneratorType
    } = useBusPageConfiguration();
    const { subscriptions, teamName } = useUserDataContext();

    const [createUserScopeConfig] = useMutation<
        CreateUserScopeConfigMutation,
        CreateUserScopeConfigMutationVariables
    >(CREATE_USER_SCOPE_CONFIG);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const createUserScope = async (
        input: ValidatedNewScopeRequest,
        source: RestudySource
    ): Promise<boolean> => {
        const scopeNameResult = displayNameToScopeName(
            input.displayName,
            teamName,
            niraBusId,
            allScopesForBus.map((s) => s.scope)
        );
        if (!scopeNameResult.success) {
            CreateUserScopeToaster.show({
                message: scopeNameResult.error,
                intent: "danger"
            });
            return false;
        }
        const scopeName = scopeNameResult.scopeName;
        const scopeViews = Array.from(
            new Set(
                input.selectedFuelTypes.map((ft) =>
                    getCapacityCostsScopeViewFromRegionAndGenerator(
                        bus.region,
                        ft
                    )
                )
            )
        );

        const subscription = subscriptions.find((s) => s.region === bus.region);
        if (!subscription) {
            CreateUserScopeToaster.show({
                message: `No subscription found for region ${bus.region}`,
                intent: "danger"
            });
            return false;
        }

        const queueProjectInputs = buildQueueProjectInsertInputs(
            input.editedQueueProjects,
            scopeViews,
            bus.region,
            scopeName
        );

        // Speculative rows ride in the same insert (and therefore the same
        // transaction) as the toggle rows: the pipeline reads them at the
        // moment it marks the scope RUNNING, so they must exist before the
        // user_scope_configs row (whose status defaults to RUN_REQUESTED)
        // becomes visible — which one transaction guarantees.
        const speculativeProjectInputs =
            buildSpeculativeQueueProjectInsertInputs(
                input.newProjects,
                scopeViews,
                bus.region,
                scopeName
            );

        // Record the hand-entered projects in the study's notes so their names
        // and buses show up in the new study's Assumptions panel.
        const newProjectsSummary =
            input.newProjects.length === 0
                ? ""
                : `\n\n**New projects added to the dispatch assumptions:**\n\n` +
                  input.newProjects
                      .map(
                          (draft) =>
                              `* ${draft.name.trim()} — ${getSpeculativeFuelTypeLabel(
                                  draft.fuelType
                              )}, ${draft.nameplateMw ?? 0} MW` +
                              (draft.chargingMw !== undefined
                                  ? ` (${draft.chargingMw} MW charging)`
                                  : "") +
                              ` at bus ${draft.poiBus?.busId ?? "—"}`
                      )
                      .join("\n");

        const busesToScopesInputs = scopeViews.map((scopeView) => ({
            scope: scopeName,
            scope_view: scopeView,
            nira_bus_id: niraBusId,
            bus_id: bus.id
        }));

        setIsSubmitting(true);

        // One mutation, one transaction. The scopes row, its user_scope_config,
        // the queue projects and the buses_to_scopes rows all commit together or
        // none of them do. This used to be two sequential mutations, so a
        // failure of the second left an orphaned scopes row behind, which the
        // app then rendered as a Nira base scope for the whole team.
        try {
            await createUserScopeConfig({
                variables: {
                    scope: scopeName,
                    display_name: input.displayName.trim(),
                    region: bus.region,
                    scope_views: scopeViews,
                    data_freshness_markdown: input.notes + newProjectsSummary,
                    subscription_id: subscription.id,
                    base_scope: input.baseScope.scope,
                    queue_projects: [
                        ...queueProjectInputs,
                        ...speculativeProjectInputs
                    ],
                    buses_to_scopes: busesToScopesInputs
                },
                refetchQueries: [
                    {
                        query: GET_SCOPES_FOR_BUS,
                        variables: { niraBusId }
                    },
                    {
                        query: GET_BUS_SCOPE_VIEWS,
                        variables: { niraBusId }
                    }
                ]
            });
        } catch (error) {
            console.error("Failed to create scope", error);
            setIsSubmitting(false);
            CreateUserScopeToaster.show({
                message: "Failed to create scope. Please try again.",
                intent: "danger"
            });
            return false;
        }

        setIsSubmitting(false);
        const displayName = input.displayName.trim();
        CreateUserScopeToaster.show({
            message: `Scope "${displayName}" created successfully.`,
            intent: "success",
            action: {
                text: "Switch to scope",
                onClick: () => {
                    segmentTrackRestudySwitchToScopeClicked(
                        niraBusId,
                        bus.region,
                        scopeName,
                        source
                    );
                    setSelectedGeneratorType(input.selectedFuelTypes[0]);
                    setSelectedScope(scopeName);
                }
            }
        });
        return true;
    };

    return { createUserScope, isSubmitting };
};
